import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { useEffect, useState, useCallback, useMemo } from "react";
import type { CarouselApi } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";

interface Banner {
  id: string;
  storage_path: string;
  storage_path_avif: string | null;
  storage_path_webp: string | null;
  is_visible: boolean;
  position: number;
  target_device: string;
}

function BannerCarousel({
  banners,
  isEager,
}: {
  banners: Banner[];
  isEager: boolean;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  const getUrls = useCallback((banner: Banner) => {
    const original = supabase.storage.from("car-images").getPublicUrl(banner.storage_path).data.publicUrl;
    const avif = banner.storage_path_avif
      ? supabase.storage.from("car-images").getPublicUrl(banner.storage_path_avif).data.publicUrl
      : null;
    const webp = banner.storage_path_webp
      ? supabase.storage.from("car-images").getPublicUrl(banner.storage_path_webp).data.publicUrl
      : null;
    return { original, avif, webp };
  }, []);

  if (banners.length === 0) return null;

  const autoplayPlugin = Autoplay({ delay: 5000, stopOnInteraction: false });

  return (
    <div className="relative w-full">
      <Carousel
        setApi={setApi}
        opts={{ loop: true }}
        plugins={banners.length > 1 ? [autoplayPlugin] : []}
        className="w-full h-full"
      >
        <CarouselContent className="-ml-0">
          {banners.map((banner, i) => {
            const urls = getUrls(banner);
            return (
              <CarouselItem key={banner.id} className="pl-0">
                <picture className="block w-full">
                  {urls.avif && <source srcSet={urls.avif} type="image/avif" />}
                  {urls.webp && <source srcSet={urls.webp} type="image/webp" />}
                  <img
                    src={urls.original}
                    alt="Banner PassCar"
                    className="w-full h-[300px] md:h-[471px] object-cover object-center"
                    loading={isEager && i === 0 ? "eager" : "lazy"}
                    fetchPriority={isEager && i === 0 ? "high" : undefined}
                  />
                </picture>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>

      {banners.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
          {banners.map((_, idx) => (
            <button
              key={idx}
              onClick={() => api?.scrollTo(idx)}
              className="relative flex items-center justify-center w-7 h-7"
              aria-label={`Ir para banner ${idx + 1}`}
            >
              <span
                className={cn(
                  "block w-2.5 h-2.5 rounded-full transition-all",
                  idx === current ? "bg-white scale-110" : "bg-white/40 hover:bg-white/70",
                )}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function BannerSection() {
  const { data: banners } = useQuery({
    queryKey: ["banners-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .eq("is_visible", true)
        .order("position", { ascending: true });
      if (error) throw error;
      return data as Banner[];
    },
  });

  const desktopBanners = useMemo(
    () => banners?.filter((b) => b.target_device === "desktop" || b.target_device === "both") || [],
    [banners]
  );

  const mobileBanners = useMemo(
    () => banners?.filter((b) => b.target_device === "mobile" || b.target_device === "both") || [],
    [banners]
  );

  if (!banners || banners.length === 0) return null;

  return (
    <section className="relative w-full top-0 mt-0 overflow-hidden">
      {/* Desktop carousel */}
      <div className="hidden md:block">
        <BannerCarousel banners={desktopBanners} isEager={true} />
      </div>

      {/* Mobile carousel — all images lazy to prevent double download */}
      <div className="block md:hidden">
        <BannerCarousel banners={mobileBanners} isEager={true} />
      </div>
    </section>
  );
}
